/** F6 Finale: the stepper over FINALE_STAGES, one Advance at a time, "Project finale", and each stage's view. Rank is the last button (OD5-5). */
import { useState } from "react";
import { FINALE_STAGES, type FinaleView } from "@bsim/contracts";
import { useRoute } from "../api/context";
import { formatPp, formatUsdPerShare } from "../format";
import { NOT_A_VERDICT } from "../results/Scoreboard";
import { GxScoreboard } from "../score/GxScoreboard";
import { Icon } from "../theme/icons";
import { GxCard } from "../theme/primitives";
import { GxNotes } from "../theme/GxNotes";
import { EmptyState } from "./EmptyState";
import { anonymised, GxReplayChart } from "../charts/ReplayChart";
import { useHiddenAiSeats } from "./useAiSeats";
import { DEAL_DECISION_WORDS, DEAL_VS_RANK_NOTE, FINALE_LABELS, OPEN_OFFER_LINE } from "../room/labels";
import { NoOffer } from "../room/NoOffer";
import type { Act } from "./useAct";

type Stage = (typeof FINALE_STAGES)[number];

export const nextStage = (stage: Stage): Stage | null => FINALE_STAGES[FINALE_STAGES.indexOf(stage) + 1] ?? null;

function StageView({ view, cohortId }: { readonly view: FinaleView; readonly cohortId: string }) {
  const hidden = useHiddenAiSeats(cohortId, view.stage);
  // Only the stage the room is on: the earlier blocks are done, and a stage that lists the AI banks under "Deal call" reads as the wrong screen.
  return (
    <div className="gx-card-b gx-fac-finale" data-testid="finale.view">
      {view.replay === null || view.stage !== "replay" ? null : <GxReplayChart replay={anonymised(view.replay, hidden)} height={220} testId="finale.replay" />}
      {view.aiBanks === null || view.stage !== "aiReveal" ? null : (
        <section><h3 className="gx-h3">AI banks revealed</h3>
          <ul className="gx-fac-list">{view.aiBanks.map((b) => <li key={b.teamId}><span><b>{b.name}</b> · {b.personaName}<br /><span className="gx-small gx-muted">{b.playbook}</span></span><span className="gx-num">{formatPp(b.valueCreatedRate, { signed: true })} official</span></li>)}</ul>
        </section>
      )}
      {view.dealCalls === null || view.stage !== "dealCall" ? null : (
        <section><h3 className="gx-h3">Deal call</h3>
          {view.dealCalls.length === 0 ? <NoOffer replay={view.replay} testId="finale.deal.none" /> : null}
          <ul className="gx-fac-list">{view.dealCalls.map((d) => <li key={d.teamId}><span><b>{d.name}</b> · {DEAL_DECISION_WORDS[d.dealCall.decision].toLowerCase()} the round {d.dealCall.offeredRound} offer of {formatUsdPerShare(d.dealCall.offerUsdPerShare)}/share</span><span className="gx-num">{formatUsdPerShare(d.dealCall.dealCallDisplayUsdPerShare, { signed: true })}/share in today's money</span><span className="gx-small gx-muted gx-num" data-testid="finale.deal.worth">On its own plan {formatUsdPerShare(d.dealCall.teamWealthUsdPerShare)} a share; its twin {formatUsdPerShare(d.dealCall.autopilotWealthUsdPerShare)}.</span></li>)}</ul>
          {view.dealCalls.length === 0 ? null : <GxNotes testId="finale.deal.note" notes={[`${DEAL_VS_RANK_NOTE} ${OPEN_OFFER_LINE}`]} />}
        </section>
      )}
      {view.recognitions === null || view.stage !== "recognitions" ? null : (
        <section><h3 className="gx-h3">Recognitions</h3>
          <ul className="gx-fac-list">{view.recognitions.map((r) => <li key={r.kind}><span><b>{r.teamNames.join(", ")}</b><br /><span className="gx-small gx-muted">{r.citation}</span></span></li>)}</ul>
        </section>
      )}
      {view.ranking === null || view.stage !== "rank" ? null : (
        <section><h3 className="gx-h3">Ranking</h3>
          <GxScoreboard banks={view.ranking.map((r) => ({ teamId: r.teamId, seatId: r.teamId, name: r.name, isAi: null, rank: r.rank, score: r.score, gateRankedLast: r.gateRankedLast }))} youSeatId={null} variant="finale" rankRule={view.rankRule} />
          <GxNotes testId="finale.verdict" notes={[NOT_A_VERDICT]} />
        </section>
      )}
    </div>
  );
}

export function FinaleTab({ cohortId, act, projectFinale }: { readonly cohortId: string; readonly act: Act; readonly projectFinale: () => void }) {
  const finale = useRoute("finaleGet", { cohortId });
  const view = finale.data;
  // One advance in flight at a time: the label comes from the server's stage, so a second click never skips one. The button is free again when the stage the server holds has moved (or the call failed).
  const [advancing, setAdvancing] = useState<Stage | null>(null);
  const busy = advancing !== null && advancing === view?.stage;
  const advance = async (from: Stage): Promise<void> => {
    setAdvancing(from);
    const res = await act("finaleAdvance", { cohortId }, { expectedStage: from }, { idempotent: true });
    if (!res.ok) setAdvancing(null);
  };
  const next = view === undefined ? null : nextStage(view.stage);
  return (
    <GxCard title="Finale" className="gx-fac-panel" testId="studio.finale">
      {view === undefined ? <EmptyState icon="room" title="The finale opens after the final release" /> : (
        <>
          <ol className="gx-fac-steps" aria-label="Finale progress (read-only)">
            {FINALE_STAGES.filter((s) => s !== "notStarted").map((s) => {
              const state = FINALE_STAGES.indexOf(s) < FINALE_STAGES.indexOf(view.stage) ? "done" : s === view.stage ? "now" : "todo";
              return <li key={s} data-status={state} aria-current={state === "now" ? "step" : undefined}>{FINALE_LABELS[s]}</li>;
            })}
          </ol>
          <div className="gx-card-b gx-fac-finale-acts">
            {next === null
              ? <span className="gx-muted">The finale is complete.</span>
              : <button type="button" className="gx-btn gx-btn--accent" data-testid="finale.advance" disabled={busy} aria-busy={busy} onClick={() => void advance(view.stage)}>{busy ? <span className="gx-spinner" aria-hidden="true" data-testid="finale.advancing" /> : null}Advance to {FINALE_LABELS[next]}</button>}
            <button type="button" className="gx-btn gx-btn--secondary" data-testid="finale.project" onClick={projectFinale}><Icon name="room" size={14} />Project finale</button>
            {next === "rank" ? <span className="gx-small gx-faint">Rank comes last. Participants' devices reveal it when you advance.</span> : null}
          </div>
          <StageView view={view} cohortId={cohortId} />
        </>
      )}
    </GxCard>
  );
}
